import { useContext, useEffect, useState } from 'react';
import { Button, Container } from "react-bootstrap";
import { ErrorBoundary } from 'react-error-boundary';
import { toast } from 'react-toastify';
import { Column, Icon, Label, Row } from '../../../components';
import AppContext from '../../../shared/components/AppContext';
import { FallbackComponent } from '../../../shared/components/FallbackComponent';
import TextInputWithLabelInline from '../../../shared/components/input/TextInputWithLabelInline';
import { useConfirmationModalContext } from "../../../shared/components/ModalConfirmationContext";
import * as constant from '../../../shared/util/constant';
import * as label from '../../../shared/util/label';
import AgencyGroupModel from '../../model/AgencyGroupModel';
import ScreenElementAgencyModel from '../../model/ScreenElementAgencyModel';
import { userSetupUtils } from '../../../Admin/utils/UserSetupUtils';
import AgencySetupGrid from "./AgencySetupGrid";
import AgencySetupService from "./AgencySetupService";
import './AgencySetup.scss';

interface Props {
    groupId: string;
    onStateChange: (value: boolean) => void;
    onCancel: () => void;
    onSave: (value: String) => void;
    onDelete: () => void;
    canCreate?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
}


const AgencySetup = (props: Props) => {

    const { canCreate = true, canUpdate = true, canDelete = true } = props;
    const groupId = props.groupId;
    const onStateChange = props.onStateChange;

    const [listOfExistingAgencies, setlistOfExistingAgencies] = useState([]);
    const [listOfAllAgencies, setListOfAgencies] = useState([]);

    const [isDisabled, setIsDisabled] = useState(false);

    const [agenciesByGroup, setAgenciesByGroup] = useState<ScreenElementAgencyModel[]>([]);
    const [allUniqueAgencies, setAllUniqueAgencies] = useState([]);
    const [listOfAgencyGroup, setListOfAgencyGroup] = useState([]);
    const service = new AgencySetupService();
    const initializeAgencyGroup: AgencyGroupModel = {
        id: '',
        groupName: ''
    }
    const intialValidationState = {
        groupName: { isValid: true, message: '' },
    };
    const [validations, setValidations] = useState(intialValidationState);

    const [agencyGroup, setAgencyGroup] = useState<AgencyGroupModel>(initializeAgencyGroup)
    const modalContext = useConfirmationModalContext();
    const agencyGroupsContext = useContext(AppContext);

    useEffect(() => {
        if (groupId == null)
            agencyGroup.groupName = "";
    }, [])

    useEffect(() => {
        setAgencyGroup(initializeAgencyGroup);
        initializeAgencyGroupList();
        setValidations(intialValidationState);
    }, [groupId]);

    const initializeAgencyGroupList = async () => {
        let listOfAgencyGroup = await userSetupUtils.getAgencyGroups()
        let listOfAgenciesByGroup = await userSetupUtils.getAgenciesByGroup(groupId);
        let agenciesList = await service.fetchAgencies();
        setListOfAgencies(agenciesList)
        listOfAgenciesByGroup.forEach(function (item) {
            listOfExistingAgencies.push(item.value);
        })

        var unique = agenciesList.filter(obj => !listOfAgenciesByGroup.some(({ value }) => obj.value === value));

        var transformedData = listOfAgenciesByGroup.map((item) => {
            return {
                agencyId: item.value,
                agencyName: item.label
            }
        });
        if (groupId != null) {
            var agency = listOfAgencyGroup?.map((item) => {
                return {
                    id: item.value,
                    groupName: item.label
                }
            }).filter(item => item.id == groupId.toString())[0];
        }
        setListOfAgencyGroup(listOfAgencyGroup);
        setAllUniqueAgencies(unique);
        if (agency) {
            setAgencyGroup(agency);
        }

        setAgenciesByGroup(transformedData);
    }

    const onCancel = async () => {
        const result = await modalContext.showConfirmation(constant.CONFIRM_EXIT)
        if (result) {
            setAgencyGroup(initializeAgencyGroup)
            agencyGroupsContext.formSavedStatus = true;
            onStateChange(false);
            props.onCancel();
        }
    }

    const checkDuplicateGroupName = () => {
        let name = agencyGroup.groupName.toLocaleUpperCase();
        let isExist = listOfAgencyGroup.filter(item => item.label.toLocaleUpperCase() == name.toLocaleUpperCase());
        if (isExist.length > 0) {
            toast.error("Group Name already Exist", {
                position: toast.POSITION.TOP_RIGHT,
                autoClose: constant.AUTO_CLOSE
            });
            return false;
        }
        return true;
    }

    const validateForSave = () => {
        let groupNameError = "";

        if (agencyGroup.groupName.length === 0) {
            groupNameError = constant.FIELD_BLANK;
        } else if (agencyGroup.groupName.length < 3) {
            groupNameError = constant.NAME_LEN_ERROR;
        }
        setValidations({
            groupName: {
                isValid: !groupNameError,
                message: groupNameError
            }
        });
        return !groupNameError
    }

    const onSave = async (update: boolean) => {
        if (validateForSave()) {
            const result = await modalContext.showConfirmation(update ? constant.AGENCY_GROUP_UPDATE : constant.AGENCY_GROUP_SAVING)
            if (result) {
                onStateChange(false);
                let isExist = checkDuplicateGroupName();
                if (isExist) {
                    if (!update)
                        agencyGroup.id = constant.DEFAULT_GUID;
                    let response = await service.saveAgencyGroup(agencyGroup, update);
                    if (response) {
                        agencyGroup.id = response.id;
                        agencyGroupsContext.formSavedStatus = true;
                        toast.success(constant.AGENCY_GROUP_SAVING_SUCCESS, {
                            position: toast.POSITION.TOP_RIGHT,
                            autoClose: constant.AUTO_CLOSE
                        });
                        onStateChange(true);
                        props.onSave(response.id);
                    }
                    else {
                        toast.error(constant.AGENCY_GROUP_SAVING_ERROR, {
                            position: toast.POSITION.TOP_RIGHT,
                            autoClose: constant.AUTO_CLOSE
                        });
                    }
                }
            }
        }
    }
    const onDelete = async () => {
        const result = await modalContext.showConfirmation(constant.AGENCY_GROUP_DELETE + `${agencyGroup?.groupName}?`);
        if (result) {
            let response = await service.deleteAgencyGroup(agencyGroup);
            if (response) {
                toast.success(constant.AGENCY_GROUP_DELETE_SUCCESS, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: constant.AUTO_CLOSE
                });
            }
            else {
                toast.error(constant.AGENCY_GROUP_DELETE_ERROR, {
                    position: toast.POSITION.TOP_RIGHT,
                    autoClose: constant.AUTO_CLOSE
                });
            }
            props.onDelete();
            agencyGroupsContext.formSavedStatus = true;
        }
    }

    const onEdit = async () => {
        onStateChange(false);
        setIsDisabled(false);
    }

    const onEntityFieldChange = (propName: string, value: string | Date | boolean) => {
        setAgencyGroup({ ...agencyGroup, [propName]: value });
    }

    return (
        <Container fluid>
            <Row className="screenPrivilegeDetails">
                <Column lg={12} md={12}>
                    <ErrorBoundary FallbackComponent={FallbackComponent}>
                        <Column lg={12} md={12} className=''>
                            <Row style={{ marginTop: '15px' }}>
                                <Column>
                                    {groupId ?
                                        <Label text={`${agencyGroup?.groupName.toLocaleUpperCase()}`} className='role-name-label'></Label>
                                        : null}
                                </Column>
                            </Row>
                            <Row className='cstPanel'>
                                <Column lg={8} className={validations.groupName.isValid ? '' : ''} style={{ marginBottom: "20px" }}>
                                    <TextInputWithLabelInline
                                        name={label.DESCRIPTION}
                                        label={label.DESCRIPTION}
                                        placeholder={label.DESCRIPTION}
                                        value={agencyGroup?.groupName}
                                        onChange={(event) => onEntityFieldChange('groupName', event.currentTarget.value)}
                                        isEditable={!isDisabled && canUpdate}
                                        type='text'
                                        labelClass='inlineLable'
                                        lglabel={4}
                                        mdlabel={4}
                                        isRequired
                                        lgText={8}
                                        mdText={8}
                                        maxLength={150}
                                        isInvalid={!validations.groupName.isValid}
                                        invalidWarning={validations.groupName.message}
                                        className='texbox-input'>
                                    </TextInputWithLabelInline>
                                </Column>

                                <Column>
                                    {groupId && !isDisabled &&
                                        <Button
                                            onClick={() => onSave(true)}
                                            className='grid-icon'
                                            outlined={true}
                                            size='small'
                                            disabled={!canUpdate}>
                                            <Icon icon="save" className="me-1" />
                                        </Button>}
                                    {groupId && isDisabled &&
                                        <Button
                                            onClick={onEdit}
                                            className='grid-icon'
                                            outlined={true}
                                            size='small'
                                            disabled={!canUpdate}>
                                            <Icon icon="edit" className="me-1" />
                                        </Button>}
                                    {groupId &&
                                        <Button
                                            style={{ marginLeft: "5px" }}
                                            onClick={onDelete}
                                            className='grid-icon'
                                            outlined={true}
                                            size='small'
                                            disabled={!canDelete}>
                                            <Icon icon="trash" className="me-1" />
                                        </Button>}
                                </Column>
                            </Row>
                            <Row>
                                <Column lg={8}>
                                    <div className='d-flex justify-content-between align-items-center'>
                                        {!groupId &&
                                            <><Button color='primary' variant='outline-primary' onClick={onCancel} className='ms-auto'>{label.CANCEL.toUpperCase()}</Button>
                                                <Button color='primary' onClick={() => onSave(false)} className='ms-2' disabled={!canCreate}>{label.SAVE.toUpperCase()}</Button></>
                                        }
                                    </div>
                                </Column>
                            </Row>

                            <Row>
                                <Column lg={12} className=''>
                                    <div>
                                        {groupId &&
                                            < AgencySetupGrid allUniqueAgencies={allUniqueAgencies} listOfAllAgencies={listOfAllAgencies} groupId={groupId} data={agenciesByGroup} canDelete={canDelete} />
                                        }
                                    </div>
                                </Column>
                            </Row>
                        </Column>
                    </ErrorBoundary>
                </Column>
            </Row>
        </Container>


    );
}

export default AgencySetup;